import { type ReactNode, useMemo } from 'react'

import { DATEFORMAT_FULL } from '@/defs/date'
import { type IconType } from '@/defs/icon'
import { ARTICLE_URLS } from '@/defs/misc'
import { type RecType } from '@/defs/rec'
import { useCardsRec } from '@/hooks/useCardsRec'
import { useReduxSelector } from '@/store/hooks'
import { isDefined } from '@/utils/commonUtil'
import { formatDate, formatSalt, formatZScore } from '@/utils/formatUtil'
import { buildRoute } from '@/utils/routeUtil'

const FLAGS = ['gameChanger', 'new', 'fiery', 'salt'] as const
type ConfigType = Record<
  (typeof FLAGS)[number],
  {
    check?: boolean
    icon: IconType
    label?: ReactNode | false
    labelLink?: string
    labelShort?: string
    textIcon?: boolean
  }
>

type Props = {
  enabled?: boolean
  rec?: RecType
}

export const useCardFlags = ({ enabled, rec }: Props) => {
  const { card, cardPartner } = useCardsRec({ enabled, rec })
  const saltLevel = useReduxSelector((s) => s.user.profile?.saltLevel) || 0
  const saltCards = [card?.salt, cardPartner?.salt].filter(isDefined)
  const salt = rec?.saltRec ?? (saltCards.length ? Math.max(...saltCards) : undefined)

  return useMemo(() => {
    const cardNew = [card, cardPartner].find((c) => c?.isNew)
    const setSlugNew = cardNew?.sets?.[0]
    const releasedAtNew = isDefined(cardNew?.releasedAt) ? formatDate(cardNew.releasedAt, DATEFORMAT_FULL) : undefined

    const CONFIG: ConfigType = {
      fiery: {
        check: (rec?.trendZscore || 0) >= 1,
        icon: 'fire',
        label: isDefined(rec?.trendZscore) && (
          <p>
            This card is trending with a high fire score (<strong>{formatZScore(rec.trendZscore)}</strong>)!
          </p>
        ),
        labelLink: ARTICLE_URLS.FIERY,
        labelShort: 'Hot',
      },
      gameChanger: {
        check: card?.isGameChanger || cardPartner?.isGameChanger,
        icon: 'gem',
        label: <p>Game Changer</p>,
        labelLink: buildRoute({ section: 'gameChangers' }),
        labelShort: 'Game Changer',
      },
      new: {
        check: !!cardNew,
        icon: 'sparkles',
        label: !!releasedAtNew && (
          <p>
            New Card (<strong>{releasedAtNew}</strong>)
          </p>
        ),
        labelLink: setSlugNew ? buildRoute({ section: 'sets', setSlug: setSlugNew }) : undefined,
        labelShort: 'New',
        textIcon: true,
      },
      salt: {
        check: !!salt && (saltLevel >= 2 || salt > 2),
        icon: 'salt',
        label: !!salt && (
          <p>
            {salt > 2 && (
              <>
                <span>High</span>{' '}
              </>
            )}
            <span>
              Salt Score (<strong>{formatSalt(salt)}</strong>)
            </span>
          </p>
        ),
        labelLink: buildRoute({ section: 'salt' }),
        labelShort: (salt || 0) > 2 ? 'High Salt' : 'Salt',
      },
    }

    return FLAGS.filter((flag) => !!CONFIG[flag].check).map((flag) => ({
      flag,
      ...CONFIG[flag],
    }))
  }, [card, cardPartner, rec, salt, saltLevel])
}
